import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Equipment } from '@/types'

interface EquipmentState {
  equipment: Equipment[]
  selectedEquipment: Equipment | null
  isLoading: boolean
  error: string | null
  lastUpdated: string | null
  healthSummary: {
    excellent: number
    good: number
    warning: number
    poor: number
    critical: number
    averageHealth: number
  }
  typeCounts: Record<Equipment['type'], number>
}

const initialState: EquipmentState = {
  equipment: [],
  selectedEquipment: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  healthSummary: {
    excellent: 0,
    good: 0,
    warning: 0,
    poor: 0,
    critical: 0,
    averageHealth: 0,
  },
  typeCounts: {
    hvac: 0,
    plumbing: 0,
    electrical: 0,
    elevators: 0,
    security: 0,
    other: 0,
  },
}

const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    setEquipment: (state, action: PayloadAction<Equipment[]>) => {
      state.equipment = action.payload
      state.lastUpdated = new Date().toISOString()
      state.error = null
      
      // Update health summary
      const healthCounts = { excellent: 0, good: 0, warning: 0, poor: 0, critical: 0 }
      const typeCounts: Record<Equipment['type'], number> = {
        hvac: 0,
        plumbing: 0,
        electrical: 0,
        elevators: 0,
        security: 0,
        other: 0,
      }
      
      let totalHealth = 0
      
      action.payload.forEach(equipment => {
        // Map operational status to health status based on health score
        let healthStatus: 'excellent' | 'good' | 'warning' | 'poor' | 'critical'
        if (equipment.healthScore >= 90) healthStatus = 'excellent'
        else if (equipment.healthScore >= 75) healthStatus = 'good'
        else if (equipment.healthScore >= 50) healthStatus = 'warning'
        else if (equipment.healthScore >= 25) healthStatus = 'poor'
        else healthStatus = 'critical'
        
        healthCounts[healthStatus]++
        typeCounts[equipment.type]++
        totalHealth += equipment.healthScore
      })
      
      state.healthSummary = {
        ...healthCounts,
        averageHealth: action.payload.length > 0 ? Math.round(totalHealth / action.payload.length) : 0,
      }
      
      state.typeCounts = typeCounts
    },
    addEquipment: (state, action: PayloadAction<Equipment>) => {
      state.equipment.push(action.payload)
      
      // Map operational status to health status based on health score
      let healthStatus: 'excellent' | 'good' | 'warning' | 'poor' | 'critical'
      if (action.payload.healthScore >= 90) healthStatus = 'excellent'
      else if (action.payload.healthScore >= 75) healthStatus = 'good'
      else if (action.payload.healthScore >= 50) healthStatus = 'warning'
      else if (action.payload.healthScore >= 25) healthStatus = 'poor'
      else healthStatus = 'critical'
      
      state.healthSummary[healthStatus]++
      state.typeCounts[action.payload.type]++
      
      // Recalculate average health
      const totalHealth = state.equipment.reduce((sum, eq) => sum + eq.healthScore, 0)
      state.healthSummary.averageHealth = Math.round(totalHealth / state.equipment.length)
      
      state.lastUpdated = new Date().toISOString()
    },
    updateEquipment: (state, action: PayloadAction<{ id: string; updates: Partial<Equipment> }>) => {
      const index = state.equipment.findIndex(eq => eq.id === action.payload.id)
      if (index !== -1) {
        const oldEquipment = state.equipment[index]
        const updatedEquipment = { ...oldEquipment, ...action.payload.updates }
        state.equipment[index] = updatedEquipment
        
        // Update health summary based on health scores
        let oldHealthStatus: 'excellent' | 'good' | 'warning' | 'poor' | 'critical'
        if (oldEquipment.healthScore >= 90) oldHealthStatus = 'excellent'
        else if (oldEquipment.healthScore >= 75) oldHealthStatus = 'good'
        else if (oldEquipment.healthScore >= 50) oldHealthStatus = 'warning'
        else if (oldEquipment.healthScore >= 25) oldHealthStatus = 'poor'
        else oldHealthStatus = 'critical'
        
        let newHealthStatus: 'excellent' | 'good' | 'warning' | 'poor' | 'critical'
        if (updatedEquipment.healthScore >= 90) newHealthStatus = 'excellent'
        else if (updatedEquipment.healthScore >= 75) newHealthStatus = 'good'
        else if (updatedEquipment.healthScore >= 50) newHealthStatus = 'warning'
        else if (updatedEquipment.healthScore >= 25) newHealthStatus = 'poor'
        else newHealthStatus = 'critical'
        
        if (oldHealthStatus !== newHealthStatus) {
          state.healthSummary[oldHealthStatus]--
          state.healthSummary[newHealthStatus]++
        }
        
        // Update type counts if type changed
        if (oldEquipment.type !== updatedEquipment.type) {
          state.typeCounts[oldEquipment.type]--
          state.typeCounts[updatedEquipment.type]++
        }
        
        // Recalculate average health if health score changed
        if (oldEquipment.healthScore !== updatedEquipment.healthScore) {
          const totalHealth = state.equipment.reduce((sum, eq) => sum + eq.healthScore, 0)
          state.healthSummary.averageHealth = Math.round(totalHealth / state.equipment.length)
        }
        
        // Update selected equipment if it's the one being updated
        if (state.selectedEquipment?.id === action.payload.id) {
          state.selectedEquipment = updatedEquipment
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    removeEquipment: (state, action: PayloadAction<string>) => {
      const index = state.equipment.findIndex(eq => eq.id === action.payload)
      if (index !== -1) {
        const equipment = state.equipment[index]
        state.equipment.splice(index, 1)
        
        // Map operational status to health status based on health score
        let healthStatus: 'excellent' | 'good' | 'warning' | 'poor' | 'critical'
        if (equipment.healthScore >= 90) healthStatus = 'excellent'
        else if (equipment.healthScore >= 75) healthStatus = 'good'
        else if (equipment.healthScore >= 50) healthStatus = 'warning'
        else if (equipment.healthScore >= 25) healthStatus = 'poor'
        else healthStatus = 'critical'
        
        state.healthSummary[healthStatus]--
        state.typeCounts[equipment.type]--
        
        // Recalculate average health
        if (state.equipment.length > 0) {
          const totalHealth = state.equipment.reduce((sum, eq) => sum + eq.healthScore, 0)
          state.healthSummary.averageHealth = Math.round(totalHealth / state.equipment.length)
        } else {
          state.healthSummary.averageHealth = 0
        }
        
        if (state.selectedEquipment?.id === action.payload) {
          state.selectedEquipment = null
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    setSelectedEquipment: (state, action: PayloadAction<Equipment | null>) => {
      state.selectedEquipment = action.payload
    },
    updateEquipmentHealthScore: (state, action: PayloadAction<{
      id: string
      healthScore: number
      status?: Equipment['status']
      trend?: Equipment['trend']
    }>) => {
      const index = state.equipment.findIndex(eq => eq.id === action.payload.id)
      if (index !== -1) {
        const oldEquipment = state.equipment[index]
        
        // Determine new status based on health score if not provided
        let newStatus = action.payload.status
        if (!newStatus) {
          if (action.payload.healthScore >= 90) newStatus = 'excellent'
          else if (action.payload.healthScore >= 75) newStatus = 'good'
          else if (action.payload.healthScore >= 50) newStatus = 'warning'
          else if (action.payload.healthScore >= 25) newStatus = 'poor'
          else newStatus = 'critical'
        }
        
        state.equipment[index] = {
          ...oldEquipment,
          healthScore: action.payload.healthScore,
          status: newStatus,
          trend: action.payload.trend || oldEquipment.trend,
        }
        
        // Update health summary based on health scores
        let oldHealthStatus: 'excellent' | 'good' | 'warning' | 'poor' | 'critical'
        if (oldEquipment.healthScore >= 90) oldHealthStatus = 'excellent'
        else if (oldEquipment.healthScore >= 75) oldHealthStatus = 'good'
        else if (oldEquipment.healthScore >= 50) oldHealthStatus = 'warning'
        else if (oldEquipment.healthScore >= 25) oldHealthStatus = 'poor'
        else oldHealthStatus = 'critical'
        
        let newHealthStatus: 'excellent' | 'good' | 'warning' | 'poor' | 'critical'
        if (action.payload.healthScore >= 90) newHealthStatus = 'excellent'
        else if (action.payload.healthScore >= 75) newHealthStatus = 'good'
        else if (action.payload.healthScore >= 50) newHealthStatus = 'warning'
        else if (action.payload.healthScore >= 25) newHealthStatus = 'poor'
        else newHealthStatus = 'critical'
        
        if (oldHealthStatus !== newHealthStatus) {
          state.healthSummary[oldHealthStatus]--
          state.healthSummary[newHealthStatus]++
        }
        
        // Recalculate average health
        const totalHealth = state.equipment.reduce((sum, eq) => sum + eq.healthScore, 0)
        state.healthSummary.averageHealth = Math.round(totalHealth / state.equipment.length)
        
        if (state.selectedEquipment?.id === action.payload.id) {
          state.selectedEquipment = state.equipment[index]
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    updateEquipmentSensorData: (state, action: PayloadAction<{
      equipmentId: string
      sensorData: Equipment['sensors']
    }>) => {
      const index = state.equipment.findIndex(eq => eq.id === action.payload.equipmentId)
      if (index !== -1) {
        state.equipment[index].sensors = action.payload.sensorData
        
        if (state.selectedEquipment?.id === action.payload.equipmentId) {
          state.selectedEquipment = state.equipment[index]
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    addMaintenanceRecord: (state, action: PayloadAction<{
      equipmentId: string
      record: Equipment['maintenanceHistory'][0]
    }>) => {
      const index = state.equipment.findIndex(eq => eq.id === action.payload.equipmentId)
      if (index !== -1) {
        state.equipment[index].maintenanceHistory.unshift(action.payload.record)
        state.equipment[index].lastMaintenance = action.payload.record.performedAt
        
        if (action.payload.record.nextMaintenanceDate) {
          state.equipment[index].nextMaintenance = action.payload.record.nextMaintenanceDate
        }
        
        if (state.selectedEquipment?.id === action.payload.equipmentId) {
          state.selectedEquipment = state.equipment[index]
        }
        
        state.lastUpdated = new Date().toISOString()
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      if (action.payload) {
        state.error = null
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
  },
})

export const {
  setEquipment,
  addEquipment,
  updateEquipment,
  removeEquipment,
  setSelectedEquipment,
  updateEquipmentHealthScore,
  updateEquipmentSensorData,
  addMaintenanceRecord,
  setLoading,
  setError,
  clearError,
} = equipmentSlice.actions

// Selectors
export const selectEquipment = (state: { equipment: EquipmentState }) => state.equipment.equipment
export const selectSelectedEquipment = (state: { equipment: EquipmentState }) => state.equipment.selectedEquipment
export const selectEquipmentLoading = (state: { equipment: EquipmentState }) => state.equipment.isLoading
export const selectEquipmentError = (state: { equipment: EquipmentState }) => state.equipment.error
export const selectEquipmentHealthSummary = (state: { equipment: EquipmentState }) => state.equipment.healthSummary
export const selectEquipmentTypeCounts = (state: { equipment: EquipmentState }) => state.equipment.typeCounts
export const selectEquipmentLastUpdated = (state: { equipment: EquipmentState }) => state.equipment.lastUpdated

// Complex selectors
export const selectEquipmentByType = (type: Equipment['type']) => (state: { equipment: EquipmentState }) =>
  state.equipment.equipment.filter(eq => eq.type === type)

export const selectEquipmentByStatus = (status: Equipment['status']) => (state: { equipment: EquipmentState }) =>
  state.equipment.equipment.filter(eq => eq.status === status)

export const selectCriticalEquipment = (state: { equipment: EquipmentState }) =>
  state.equipment.equipment.filter(eq => eq.status === 'critical')

export const selectEquipmentWithAlerts = (state: { equipment: EquipmentState }) =>
  state.equipment.equipment.filter(eq => eq.activeAlerts > 0)

export const selectEquipmentByHealthRange = (min: number, max: number) => (state: { equipment: EquipmentState }) =>
  state.equipment.equipment.filter(eq => eq.healthScore >= min && eq.healthScore <= max)

export const selectEquipmentDueForMaintenance = (state: { equipment: EquipmentState }) => {
  const now = new Date()
  return state.equipment.equipment.filter(eq => {
    if (!eq.nextMaintenance) return false
    const nextMaintenance = new Date(eq.nextMaintenance)
    const daysUntilMaintenance = Math.ceil((nextMaintenance.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilMaintenance <= 7 // Due within a week
  })
}

export default equipmentSlice.reducer